import { useState } from "react";
import AnMultipleSuggestionInputBox from "../../../input_utils/AnMultipleSuggestionBox";
import { Popup } from "../../../../design_system/components/Popup/Popup";
import { MENTION_TYPES } from "../../../utils/AllMentionTypes";
import { Radio } from "../../../../design_system/components/SelectionControls/Radio";
import PostPreview from "./PostPreview";
import { InputBox } from "../../../../design_system/components/Input Fields/InputBox";
import { InputArea } from "../../../../design_system/components/Input Fields/InputArea";
import ConditionalRender from "../../../../ConditionalRender";
import { SingleDateFilterDropdown } from "../../../admin_views/teams/singleDateFilter";
import { dateFormator, datepicker_input_format } from "../../../utils/dateUtils";
import { apiAgent } from "../../../../axiosConfig";
import { isTeamContextHandled } from "../../../../teamContext/teamSwitchService";
import { useToast } from "../../../../design_system/components/Toast/ToastContext";
import { Badge } from "../../../../design_system/components/Input Fields/Badge.jsx";


const DEFAULT_LABELS = { popupName: "Publish News", submitBtn: "Publish Article" }

const defaultArticle = { description: "", url: "", image_url: "", title: "", published_at: "", is_published_at_inaccurate: false, prospects: [], mentioned_for: "" };
const initialArticleState = (aggregateData) => ({
    ...defaultArticle,
    ...aggregateData?.sharable_article,
    prospects: aggregateData?.prospects || [],
    mentioned_for: aggregateData?.mentioned_for || "",
});

const initialPopupState = { isLoading: false, errors: {}, showDateDropdown: false, showPreview: false };

const NewsPublishPopup = ({
    onCloseCallback,
    labels = {},
    aggregateData,
    onPublishCallback,
    mode = "publish",
    publish_url,
    ...props
}) => {
    const popupLabels = { ...DEFAULT_LABELS, ...labels }
    const [article, setArticle] = useState(() => initialArticleState(aggregateData));
    const [popupState, setPopupState] = useState(initialPopupState);
    const updatePopupState = (payload) => setPopupState(prev => ({ ...prev, ...payload }));
    const toast = useToast()

    const setArticleField = (key, value) => {
        setArticle(prev => ({ ...prev, [key]: value }));
        updatePopupState({ errors: {} });
    };

    const handlePostPublish = () => {
        const tagged_prospect_ids = article.prospects.map((item) => item.id);
        let errors = {};
        if (tagged_prospect_ids.length === 0) errors["publish_error_message"] = "It is mandatory to tag prospects to create post";
        if (!article.description) errors["description"] = "Description can't be empty";
        if (!article.url) errors["url"] = "Article url can't be empty";
        if (Object.keys(errors).length > 0) {
            updatePopupState({ errors });
            return;
        }
        updatePopupState({ isLoading: true, errors: {} });
        let data_params = {
            tagged_prospect_ids,
            mentioned_for: article.mentioned_for,
            published_at: datepicker_input_format(new Date(article.published_at)),
            article_title: article.title,
            article_description: article.description,
            check_pub_guideline_ack: !!props.checkGuidelineAcknowledgement,
        }
        apiAgent({
            method: "post",
            url: publish_url,
            data: data_params
        }).then(res => {
            onCloseCallback()
            updatePopupState({ isLoading: false, errors: {} })
            onPublishCallback?.(aggregateData, res.data)
        }).catch(err => {
            if (isTeamContextHandled(err)) {
                updatePopupState({ isLoading: false, errors: {} });
                return;
            }
            if (err.response?.data?.error === 'ack_failed') props.renderGuidelinesUpdateWarning(err.response.data.data);
            updatePopupState({ isLoading: false, errors: { submitError: "Post was not created successfully" } })
        })
    }

    return (
        <Popup
            name={popupLabels.popupName}
            handleOnClosePopup={onCloseCallback}
            maxWidth={900}
            customContentStyle={{ padding: "0px" }}
            primaryAction={{
                label: popupLabels.submitBtn,
                onClick: handlePostPublish,
                isLoading: popupState.isLoading,
                error: popupState.errors?.publish_error_message,
                helperText: popupState.errors?.publish_error_message,
                showHelperText: true
            }}
            secondaryAction={{
                label: "Cancel",
                onClick: onCloseCallback
            }}
        >
            {/* ─── Two-column body ─── */}
            <div className="flex publish-popup__body">

                {/* ─── Left: Prospect search + Mention types ─── */}
                <div className="flex flex-col w-full overflow-hidden publish-popup__left popup-col">
                    <div className="publish-popup__section">
                        <AnMultipleSuggestionInputBox
                            label={<span className="body-s-semibold color-text-secondary">Add prospects related to news article</span>}
                            inputClass="an_search_box"
                            placeholder="Search Prospects"
                            suggestionUrl="/prospects/search?team_search=true"
                            selectedOptions={article.prospects}
                            callback={(options) => setArticle(prev => ({ ...prev, prospects: [...options] }))}
                            theme="tagged_options"
                        />
                    </div>
                    <>
                        <div className="newsfeed__divider publish-popup__section-divider"></div>
                        <div className="body-s-semibold publish-popup__mention-text">
                            Mentioned for:{" "}
                            {article.mentioned_for && (
                                <span className="body-m-semibold">{article.mentioned_for}</span>
                            )}
                        </div>
                        <div className="flex-1 scroll-y min-h-0 publish-popup__mention-scroll popup-scroll-section">
                            {MENTION_TYPES.map((mention_type, index) => (
                                <div key={index} className={index > 0 ? "publish-popup__mention-item" : ""}>
                                    <Radio
                                        label={mention_type.text}
                                        name="mention"
                                        value={mention_type.value}
                                        checked={article.mentioned_for === mention_type.value}
                                        onChange={() => setArticle(prev => ({ ...prev, mentioned_for: mention_type.value }))}
                                    />
                                </div>
                            ))}
                        </div>
                    </>
                </div>

                {/* ─── Vertical divider ─── */}
                <div className="publish-popup__vertical-divider"></div>

                {/* ─── Right: Article fields ─── */}
                <div className="flex-1 publish-popup__right">
                    <InputBox
                        label="Article link"
                        showLabel
                        Size="Large"
                        error={popupState.errors?.url}
                        helperText={popupState.errors?.url}
                        showHelperText={!!popupState.errors?.url}
                        customStyle={{ maxWidth: 508 }}
                        disabled
                        type="text"
                        value={article.url}
                        placeholder="Article url"
                    />
                    <div className="publish-popup__copy-link">
                        <div className="link-default support-m cursor-pointer publish-popup__copy-link-text" onClick={() => { navigator.clipboard.writeText(article.url); toast.success("Article link copied", { duration: 1500 }) }}>
                            Copy Link
                        </div>
                    </div>
                    <div className="flex items-start gap-m publish-popup__date-row">
                        <div className="publish-popup__date-wrapper">
                            <InputBox
                                label="Article Published Date"
                                showLabel
                                Size="Large"
                                type="text"
                                value={dateFormator(new Date(article.published_at), { type: "input-format" }, "-")}
                                onClick={() => updatePopupState({ showDateDropdown: true })}
                                placeholder="dd/mm/yyyy"
                                readOnly
                                innerInputStyle={{ cursor: 'pointer' }}
                            />
                            <ConditionalRender if={popupState.showDateDropdown}>
                                <SingleDateFilterDropdown
                                    toggleDropDown={() => updatePopupState({ showDateDropdown: !popupState.showDateDropdown })}
                                    currDate={new Date(article.published_at || new Date())}
                                    setCurrDate={(date) => {
                                        setArticleField("published_at", dateFormator(new Date(date), { type: "input-format" }, "-"));
                                        updatePopupState({ showDateDropdown: false });
                                    }}
                                />
                            </ConditionalRender>
                        </div>
                        {article.is_published_at_inaccurate && mode !== 'edit' && (
                            <Badge type="Error" label="Not accurate" fontClass="support-m" customStyle={{borderRadius: 'var(--radius-pill)', paddingTop: 2, paddingBottom: 2}} />
                        )}
                    </div>

                    <InputArea
                        label="Article Title"
                        showLabel
                        Size="Medium"
                        customStyle={{ marginTop: "var(--space-m)" }}
                        value={article.title || ""}
                        handleOnChange={(e) => setArticleField("title", e.target.value)}
                    />

                    <InputArea
                        label="Article Description"
                        showLabel
                        Size="Large"
                        error={popupState.errors?.description}
                        helperText={popupState.errors?.description}
                        showHelperText={!!popupState.errors?.description}
                        customStyle={{ marginTop: "var(--space-m)" }}
                        value={article.description || ""}
                        handleOnChange={(e) => setArticleField("description", e.target.value)}
                    />
                </div>
            </div>

            <PostPreview
                article={article}
                isOpen={popupState.showPreview}
                onToggle={() => updatePopupState({ showPreview: !popupState.showPreview })}
            />
        </Popup>
    )
}

export default NewsPublishPopup
